import React, { useEffect, useRef, useState } from "react";
import Select, { SelectInstance } from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Loader2 } from "lucide-react";
import { RegionType } from "types/RegionType";
import { BusinessType } from "types/BusinessType";
import { Common } from "helpers/common";
import {
  getBusinessById,
  getStates,
  getDistricts,
  getCities,
  updateBusiness,
  addBusiness,
  getSalesReps,
  getSalesRepByBusinessId,
  addSalesRep,
  updateSalesRep,
  deleteSalesRep
} from "../../../helpers/backend_helper";

const getErrorMessage = (error: any): string => {
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) return error[0];
  return '';
};

interface BusinessDetailFormProps {
  businessId: string;   
  userId: string;
  onSave: (result: any) => void;
}

export const BusinessDetailForm: React.FC<BusinessDetailFormProps> = ({ businessId, userId, onSave }) => {
  const [businessInfo, setBusinessInfo] = useState<BusinessType>();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const commonHelper = new Common();
  const districtRef = useRef<SelectInstance<RegionType> | null>(null);
  const cityRef = useRef<SelectInstance<RegionType> | null>(null);

  const [states, setStates] = useState<RegionType[]>([]);
  const [districts, setDistricts] = useState<RegionType[]>([]);
  const [cities, setCities] = useState<RegionType[]>([]);

  const [selectedState, setSelectedState] = useState<any>();
  const [selectedCity, setSelectedCity] = useState<any>();
  const [selectedDistrict, setSelectedDistrict] = useState<any>();
  const [salesReps, setSalesReps] = useState<any[]>([]);
  const [selectedSalesRep, setSelectedSalesRep] = useState<any>();
  const [businessSalesRepId, setBusinessSalesRepId] = useState<number>(0);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    //if (businessInfo?.id) {
      loadStates();
    //}
  }, [businessInfo?.id]);

  const loadData = async () => {
    await loadBusinessInfo();
    await loadSalesReps();
  };

  const loadBusinessInfo = async () => {
    try {
      const response = await getBusinessById(businessId);
      const responseData = response.data as BusinessType[];
      setBusinessInfo(responseData[0]);
    } catch (error) {
      console.error("Error loading business info", error);
    }
  };

  const loadSalesReps = async () => {
    let response = await getSalesReps();
    if (response.success) {
      const items = [{ value: '', label: 'Select' }, ...(response.data as any[]).map(item => ({ value: item.id, label: item.fullName }))];
      setSalesReps(items);
      loadSalesRepByBusinessId(items);
    }
  };

  const loadSalesRepByBusinessId = async (salesRepre: any[]) => {
    try {
      let response = await getSalesRepByBusinessId(businessId);
      if (response.success) {
        const resSalesRep = response.data && response.data[0] as any;
        const selectedSalesRep = salesRepre?.find(i => i.value == resSalesRep?.userId);
        setSelectedSalesRep(selectedSalesRep);
        setBusinessSalesRepId(resSalesRep?.id);
      }
    } catch (e) { }
  };

  const saveSalesRep = async (businessId: number) => {
    if (!selectedSalesRep) return;

    if (selectedSalesRep?.value == '' && businessSalesRepId > 0) {
      const response = await deleteSalesRep(businessSalesRepId);
      return;
    }

    try {
      const data = {
        businessId: businessId,
        userId: selectedSalesRep.value,
        isActive: true,
      };
      const response = businessSalesRepId > 0 ? 
        await updateSalesRep(data, businessSalesRepId) : 
        await addSalesRep(data);
    } catch (error) {
      commonHelper.showErrorMessage(error as string);
    }
    setIsSaving(false);
  };

  const loadStates = async () => {
    if (states.length === 0) {
      let response = await getStates();
      if (response.success) {
        const items = [{ value: '', label: 'Select' }, ...(response.data as any[]).map(item => ({ value: item.id, label: item.name }))];
        setStates(items);
        if (businessInfo?.stateId) {
          setSelectedState(items?.find(i => i.value == businessInfo?.stateId));
        }
      }

      if (businessInfo?.id) {
        await loadDistrict(businessInfo.stateId.toString());
        await loadCities(businessInfo.districtId.toString());
      }
    }
  };

  const loadDistrict = async (stateId: string) => {
    if (!stateId) return;

    try {
      let response = await getDistricts('value=' + stateId);
      if (response.success) {
        const items = [{ value: '', label: 'Select' }, ...(response.data as any[]).map(item => ({ value: item.id, label: item.name }))];
        setDistricts(items);
        if (businessInfo?.districtId) {
          setSelectedDistrict(items?.find(i => i.value == businessInfo?.districtId));
        }
      }
    } catch (err) {
      commonHelper.showErrorMessage('No district found for selected state.');
    }
  };

  const loadCities = async (districtId: string) => {
    if (!districtId) return;

    try {
      let response = await getCities('value=' + districtId);
      if (response.success) {
        const items = [{ value: '', label: 'Select' }, ...(response.data as any[]).map(item => ({ value: item.id, label: item.name }))];
        setCities(items);
        if (businessInfo?.cityId) {
          setSelectedCity(items?.find(i => i.value == businessInfo?.cityId));
        }
      }
    } catch (err) {
      commonHelper.showErrorMessage('No city found for selected district.');
    }
  };

  const handleStateSelection = async (item: any) => {
    districtRef?.current?.clearValue();
    setSelectedState(item);
    setDistricts([]);
    setSelectedDistrict({});
    validation.setFieldValue('stateId', item.value);
    loadDistrict(item.value);
  };

  const handleDistrictSelection = async (item: any) => {
    if (item) {
      cityRef?.current?.clearValue();
      setSelectedDistrict(item);
      setSelectedCity({} as RegionType);
      setCities([]);
      validation.setFieldValue('districtId', item.value);
      loadCities(item.value);
    }
  };

  const handleCitySelection = (item: any) => {
    setSelectedCity(item);
    if (item) {
      validation.setFieldValue('cityId', item.value);
    }
  };

  const handleSalesRepSelection = (item: any) => {
    setSelectedSalesRep(item);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: (businessInfo && businessInfo?.id) || "0",
      businessName: (businessInfo && businessInfo?.businessName) || "",
      gstNo: (businessInfo && businessInfo?.gstNo) || "",
      pan: (businessInfo && businessInfo?.pan) || "",
      ownerName: (businessInfo && businessInfo?.ownerName) || "",
      businessEstablishmentYear: (businessInfo && businessInfo?.businessEstablishmentYear) || "2025",
      businessType: (businessInfo && businessInfo?.businessType) || "",
      addressLine1: (businessInfo && businessInfo?.addressLine1) || "",
      addressLine2: (businessInfo && businessInfo?.addressLine2) || "",
      landmark: (businessInfo && businessInfo?.landmark) || "",
      stateId: (businessInfo && businessInfo?.stateId) || "",
      districtId: (businessInfo && businessInfo?.districtId) || "",
      cityId: (businessInfo && businessInfo?.cityId) || "",
      cityName: (businessInfo && businessInfo?.cityName) || "",
      countryId: (businessInfo && businessInfo?.countryId) || 1,
      pinCode: (businessInfo && businessInfo?.pinCode) || "",
      businessLogo: (businessInfo && businessInfo?.businessLogo) || "",
      businessDescription: (businessInfo && businessInfo?.businessDescription) || "",
      businessTagLine: (businessInfo && businessInfo?.businessTagLine) || "",
      contactPerson: (businessInfo && businessInfo?.contactPerson) || "",
      contactPhone: (businessInfo && businessInfo?.contactPhone) || "",
      contactEmail: (businessInfo && businessInfo?.contactEmail) || "",
      website: (businessInfo && businessInfo?.website) || "",
      additionalDiscount: (businessInfo && businessInfo?.additionalDiscount) || "0",
      userId:  userId,
      isActive: true,
      docStatus: "Verified",
    },
    validationSchema: Yup.object({
      businessName: Yup.string().required("Business name is required"),
      ownerName: Yup.string().required("Owner name is required"),
      //gstNo: Yup.string().required("GST number is required"),
      //pan: Yup.string().required("PAN number is required"),
      addressLine1: Yup.string().required("Address is required"),
      stateId: Yup.string().required("State is required"),
      districtId: Yup.string().required("District is required"),
      cityId: Yup.string().required("City is required"),
      pinCode: Yup.string().required("Pin code is required"),
      contactPerson: Yup.string().required("Contact person is required"),
      contactPhone: Yup.string().required("Contact phone is required"),
      additionalDiscount: Yup.string().required("Additional discount is required"),
    }),
    onSubmit: async (values: any) => {
      setIsSaving(true);
      try {
        let data = {
          ...values,
        };

        if(userId && userId != '0') {
          data.userId = userId;
        }else{
          delete data.userId;
        }

        if(businessInfo?.id && businessInfo?.id > 0) {
          data.id = businessInfo?.id;
        }else{
          delete data.id;
        }

        if (!data.pan?.toString?.()?.trim()) {
           data.pan=null;
        }

        
        if (!data.gstNo?.toString?.()?.trim()) {
          data.gstNo=null;
        }
        const response= await (businessInfo && businessInfo?.id > 0 ? updateBusiness(data) : addBusiness(data));
        await saveSalesRep(response.data.id);
        commonHelper.showSuccessMessage(`Business info has been successfully updated.`);
        onSave(values);
      } catch (error: any) {
        console.error("API-Error", error.response?.data);
        commonHelper.showErrorMessage(error.response?.data?.message || "Error updating business");
      } finally {
        setIsSaving(false);
      }
    },
  });

  return (
    <form action="#!" onSubmit={(e) => {
      e.preventDefault();
      validation.handleSubmit();
      return false;
    }}>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="xl:col-span-4">
          <label htmlFor="businessName" className="text-base font-medium">Business Name<span className='redColor'>*</span></label>
          <input type="text" id="businessName"
            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            placeholder="Business Name"
            name="businessName"
            maxLength={100}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.businessName || ""}
          />
          {validation.touched.businessName && validation.errors.businessName ? (
            <p className="text-red-400">{getErrorMessage(validation.errors.businessName)}</p>
          ) : null}
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="ownerName" className="inline-block mb-1 text-base font-medium">Owner Name<span className='redColor'>*</span></label>
          <input id="ownerName" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            placeholder="Owner Name"
            name="ownerName"
            maxLength={30}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.ownerName || ""}
          />
          {validation.touched.ownerName && validation.errors.ownerName ? (
            <p className="text-red-400">{getErrorMessage(validation.errors.ownerName)}</p>
          ) : null}
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="gstNo" className="inline-block mb-1 text-base font-medium">GST No<span className='redColor'>*</span></label>
          <input id="gstNo"
            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            placeholder="GST No"
            name="gstNo"
            maxLength={15}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.gstNo || ""}
          />
          {validation.touched.gstNo && validation.errors.gstNo ? (
            <p className="text-red-400">{getErrorMessage(validation.errors.gstNo)}</p>
          ) : null}
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="pan" className="inline-block mb-1 text-base font-medium">PAN No<span className='redColor'>*</span></label>
          <input id="pan"
            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            placeholder="PAN No"
            name="pan"
            maxLength={10}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.pan || ""}
          />
          {validation.touched.pan && validation.errors.pan ? (
            <p className="text-red-400">{getErrorMessage(validation.errors.pan)}</p>
          ) : null}
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="addressLine1" className="inline-block mb-1 text-base font-medium">Address 1<span className='redColor'>*</span></label>
          <input id="addressLine1" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Address1"
            name="addressLine1"
            maxLength={100}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.addressLine1 || ""}
          />
          {validation.touched.addressLine1 && validation.errors.addressLine1 ? (
            <p className="text-red-400">{getErrorMessage(validation.errors.addressLine1)}</p>
          ) : null}
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="addressLine2" className="inline-block mb-1 text-base font-medium">Address 2<span className='redColor'>*</span></label>
          <input id="addressLine2" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Address2"
            name="addressLine2"
            maxLength={100}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.addressLine2 || ""}
          />
          {validation.touched.addressLine2 && validation.errors.addressLine2 ? (
            <p className="text-red-400">{getErrorMessage(validation.errors.addressLine2)}</p>
          ) : null}
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="stateId" className="inline-block mb-2 text-base font-medium">State<span className='redColor'>*</span></label>
          <Select
            className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            defaultValue={selectedState}
            options={states}
            isSearchable={true}
            name="stateId"
            id="stateId"
            value={selectedState}
            onChange={handleStateSelection}
          />
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="districtId" className="inline-block mb-2 text-base font-medium">District<span className='redColor'>*</span></label>
          <Select
            className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            options={districts}
            ref={districtRef}
            isSearchable={true}
            name="districtId"
            id="districtId"
            value={selectedDistrict}
            onChange={handleDistrictSelection}
          />
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="cityId" className="inline-block mb-2 text-base font-medium">City<span className='redColor'>*</span></label>
          <Select
            className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            options={cities}
            ref={cityRef}
            isSearchable={true}
            name="cityId"
            id="cityId"
            value={selectedCity}
            onChange={handleCitySelection}
          />
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="pinCode" className="inline-block text-base font-medium">Pin Code<span className='redColor'>*</span></label>
          <input id="pinCode" 
            type="text" 
            pattern="[0-9]*"
            inputMode="numeric"
            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            placeholder="Pin Code"
            name="pinCode"
            maxLength={7}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              validation.setFieldValue('pinCode', value);
            }}
            onBlur={validation.handleBlur}
            value={validation.values.pinCode || ""}
          />
          {validation.touched.pinCode && validation.errors.pinCode ? (
            <p className="text-red-400">{getErrorMessage(validation.errors.pinCode)}</p>
          ) : null}
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="contactPerson" className="inline-block mb-1 text-base font-medium">Contact Person<span className='redColor'>*</span></label>
          <input id="contactPerson" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            placeholder="Contact Person"
            name="contactPerson"
            maxLength={30}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.contactPerson || ""}
          />
          {validation.touched.contactPerson && validation.errors.contactPerson ? (
            <p className="text-red-400">{getErrorMessage(validation.errors.contactPerson)}</p>
          ) : null}
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="contactPhone" className="inline-block text-base font-medium">Phone No.<span className='redColor'>*</span></label>
          <input id="contactPhone" 
            type="text"
            pattern="[0-9]*"
            inputMode="numeric"
            className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" 
            placeholder="Phone Number"
            name="contactPhone"
            maxLength={10}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              validation.setFieldValue('contactPhone', value);
            }}
            onBlur={validation.handleBlur}
            value={validation.values.contactPhone || ""}
          />
          {validation.touched.contactPhone && validation.errors.contactPhone ? (
            <p className="text-red-400">{getErrorMessage(validation.errors.contactPhone)}</p>
          ) : null}
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="additionalDiscount" className="inline-block text-base font-medium">Additional Discount<span className='redColor'>*</span></label>
          <input id="additionalDiscount" type="number" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            placeholder="5"
            name="additionalDiscount"
            maxLength={2}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.additionalDiscount || ""}
          />
          {validation.touched.additionalDiscount && validation.errors.additionalDiscount ? (
            <p className="text-red-400">{getErrorMessage(validation.errors.additionalDiscount)}</p>
          ) : null}
        </div>

        <div className="xl:col-span-4">
          <label htmlFor="salesRep" className="inline-block text-base font-medium">Sales Representative</label>
          <Select
            className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
            defaultValue={selectedSalesRep}
            options={salesReps}
            isSearchable={true}
            name="salesRep"
            id="salesRep"
            value={selectedSalesRep}
            onChange={handleSalesRepSelection}
            styles={{
              menu: (provided) => ({
                ...provided,
                zIndex: 9999,
                position: 'absolute'
              }),
              menuPortal: (provided) => ({
                ...provided,
                zIndex: 9999
              })
            }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div className="xl:col-span-4">
          <div className="flex justify-end gap-2 mt-4">
            {!isSaving ? (
              <button 
                type="submit" 
                className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                Save
              </button>
            ) : (
              <button 
                type="button" 
                disabled 
                className="flex items-center text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                Saving...
              </button>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}; 